"use client";

import {
  RoundFormField,
  RoundFormFieldProps,
} from "@/components/FormFieldRendering/RoundRendering";
import {
  StandardFormFieldProps,
  StandardFormField,
} from "@/components/FormFieldRendering/StandardFormRendering";
import { Dropdown } from "@/types/General/Dropdown";
import {
  InputProps as HeadlessInputProps,
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  Input as HeadlessInput,
} from "@headlessui/react";
import React, { FC, RefObject, useRef } from "react";
import cn from "classnames";
import { RectangularFormField } from "@/components/FormFieldRendering";
import { CaretDownLine, CheckMarkThin } from "@/icons";

type BaseInputProps = HeadlessInputProps & {
  ref?: RefObject<HTMLInputElement | null>;
  diallingCodesClassName?: string;
  diallingCodes?: Dropdown[];
  diallingCodeValue?: Dropdown;
  onDiallingCodeChange?: (value: Dropdown) => void;
};
const BasePhoneInput: FC<BaseInputProps> = ({
  diallingCodesClassName,
  diallingCodes,
  diallingCodeValue,
  onDiallingCodeChange,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex w-full" ref={containerRef}>
      {Number(diallingCodes?.length) > 0 && (
        <Listbox
          name="diallingCode"
          value={diallingCodeValue}
          by="value"
          onChange={onDiallingCodeChange}
        >
          <ListboxButton
            role="combobox"
            aria-required={props.required}
            className="border-swdc-silver-200 text-swdc-black-600 hover:bg-swdc-cool-gray-200 data-open:outline-swdc-blue-400 mr-2 flex shrink-0 appearance-none items-center justify-between gap-1 border-r pr-2 text-left data-open:outline-1"
          >
            {({ value }) => (
              <>
                {value?.value || value?.label}
                <CaretDownLine
                  height={18}
                  fill="var(--color-swdc-black)"
                  aria-hidden
                />
              </>
            )}
          </ListboxButton>
          <ListboxOptions
            className={cn(
              "border-swdc-gray-100 bg-swdc-white text-swdc-black-600 shadow-dropdown z-40 !max-h-60 overflow-hidden border [--anchor-gap:var(--spacing-4)] focus:outline-none",
              diallingCodesClassName,
            )}
            anchor="bottom start"
            transition
            unmount={false}
            aria-required={props.required}
          >
            {diallingCodes?.map((code) => (
              <ListboxOption
                className="text-body-m data-focus:not-data-disabled:outline-swdc-blue-400 data-selected:not-data-disabled:bg-swdc-silver-200 data-selected:data-focus:not-data-disabled:bg-swdc-silver-200 text-swdc-black-600 data-active:not-data-disabled:bg-swdc-gray-50 data-disabled:text-swdc-gray-200 relative flex w-full cursor-pointer flex-row items-center justify-between py-2 pr-12 pl-2 select-none data-disabled:cursor-not-allowed data-focus:outline-2 data-focus:-outline-offset-2 data-selected:not-data-disabled:font-bold lg:px-4"
                key={`${code.label}-${code.value}`}
                value={code}
                disabled={code.disabled}
                style={{
                  width: containerRef.current?.clientWidth,
                }}
              >
                {({ selected }) => (
                  <>
                    {code.label}
                    {selected && (
                      <CheckMarkThin className="size-4.5" aria-hidden />
                    )}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>
      )}
      <HeadlessInput {...props} />
    </div>
  );
};
export type PhoneInputProps = {
  type: "tel";
} & (
  | ({
      variant: "form";
    } & Omit<
      StandardFormFieldProps<BaseInputProps>,
      "formControl" | "withFixedLabel"
    >)
  | ({
      variant: "round";
    } & Omit<RoundFormFieldProps<BaseInputProps>, "formControl">)
  | ({
      variant: "rectangular";
    } & Omit<RectangularFormField<BaseInputProps>, "formControl">)
  | ({
      variant: "unstyled";
    } & BaseInputProps)
);
/**
 * DO NOT USE!
 * USE THE FACADE COMPONENT `Input` INSTEAD.
 */
export const PhoneInput: FC<PhoneInputProps> = ({ variant, ...props }) => {
  switch (variant) {
    case "form":
      return (
        <StandardFormField<HTMLInputElement, BaseInputProps>
          className="w-full outline-none"
          formControl={BasePhoneInput}
          withFixedLabel={Number(props.diallingCodes?.length) > 0}
          diallingCodesClassName="rounded-2xl"
          {...props}
        />
      );
    case "round":
      return (
        <RoundFormField
          className="w-full outline-none"
          formControl={BasePhoneInput}
          {...props}
        />
      );
    case "rectangular":
      return (
        <RectangularFormField
          className="w-full py-2 outline-none"
          formControl={BasePhoneInput}
          {...props}
        />
      );
    case "unstyled":
    default:
      return <BasePhoneInput {...props} />;
  }
};
